import { useNavigate } from "react-router-dom";
import { type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IconChip } from "@/components/ui/card";

/**
 * Shared chrome for the PUBLIC (unauthenticated) pages — the landing page and
 * the Literature page, with the legal pages and the landing A/C variants still
 * to come.
 *
 * Everything here was moved verbatim out of src/pages/Landing.tsx, keeping the
 * names and signatures identical so the call sites there did not change. These
 * are full-bleed and responsive: one column on phones, two from 900px up. The
 * authenticated app is phone-framed instead (see PhoneFrame in App.tsx).
 */

export const INNER = "mx-auto w-full max-w-[480px] px-6 min-[900px]:max-w-[1024px]";
export const EYEBROW =
  "text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground";
export const BODY = "text-[15px] leading-[1.6] text-muted-foreground [text-wrap:pretty]";

/** A full-bleed section; `secondary` alternates the background down the page. */
export function Band({
  secondary,
  borderTop,
  borderBottom,
  className,
  children,
}: {
  secondary?: boolean;
  borderTop?: boolean;
  borderBottom?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className={[
        secondary ? "bg-secondary" : "",
        borderTop ? "border-t border-border" : "",
        borderBottom ? "border-b border-border" : "",
        className ?? "",
      ].join(" ")}
    >
      {children}
    </section>
  );
}

export function SectionHeading({ eyebrow, title }: { eyebrow?: string; title?: string }) {
  return (
    <>
      {eyebrow && <p className={EYEBROW}>{eyebrow}</p>}
      {title && (
        <h2 className="mb-3 mt-2 text-[22px] font-semibold leading-[1.3] text-foreground [text-wrap:pretty]">
          {title}
        </h2>
      )}
    </>
  );
}

export function CtaBlock({ note = true }: { note?: boolean }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-2.5 min-[900px]:max-w-[340px]">
      <Button size="xl" fullWidth onClick={() => navigate("/signin")}>
        Begin Privately
      </Button>
      {note && (
        <p className="text-center text-[13px] text-muted-foreground">
          Free while we launch · Only an email to start
        </p>
      )}
    </div>
  );
}

/** A framed mock of a real in-app surface, with a caption under it. */
export function Glimpse({
  caption,
  children,
}: {
  caption: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="overflow-hidden rounded-[20px] border border-border bg-background p-[18px] shadow-md">
        {children}
      </div>
      <p className="text-center text-xs text-muted-foreground">{caption}</p>
    </div>
  );
}

export type Bit = { icon: LucideIcon; t: string; d: string };

export function BitList({ bits }: { bits: Bit[] }) {
  return (
    <div className="flex flex-col gap-4">
      {bits.map(({ icon, t, d }) => (
        <div key={t} className="flex items-start gap-3.5">
          <IconChip icon={icon} tint="primary" />
          <div className="flex-1">
            <p className="text-[15px] font-semibold text-foreground">{t}</p>
            <p className="mt-0.5 text-sm leading-[1.55] text-muted-foreground">{d}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
